import React, { useCallback, useState } from 'react';
import Document from '@uber/base-icons/document';
import { useStyletron } from 'baseui';
import type { OnChangeParams, Value } from 'baseui/select';
import { useDrawer } from '../../../common/multi-level-drawer';
import { isFrameClassificationLabel } from '../../../common/side-panel/utils/utils';
import { TabsM3, TASK_QUESTIONNAIRE_ID, TASK_QUESTIONNAIRE_TITLE } from '../../side-panel/const';
import { useLabelListData } from '../../side-panel/hooks/use-label-list-data';
import { LabelSearchSelect } from '../../side-panel/labels/label-search-select';
import { RequiredLabelBadge } from '../../side-panel/labels/required-label-badge';
import { useSidePanel } from '../../side-panel/side-panel-provider';
import { LabelList } from './label-list';
export const LabelPanel = () => {
  const [css, theme] = useStyletron();
  const {
    additionalOptionsComponent,
    taskQuestionnaireComponent
  } = useSidePanel();
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<Value>([]);
  const {
    open,
    close
  } = useDrawer();
  const openTaskQuestionnaire = useCallback(() => {
    if (!taskQuestionnaireComponent) return;
    open({
      id: TASK_QUESTIONNAIRE_ID,
      title: TASK_QUESTIONNAIRE_TITLE,
      titleStartEnhancer: <Document size={16} />,
      content: ({
        onClose
      }) => taskQuestionnaireComponent.Component?.({
        onClose
      }),
      onClose: close,
      styleOverrides: {
        content: {
          padding: '0px'
        }
      }
    });
  }, [taskQuestionnaireComponent, open, close]);
  const {
    filterOptions,
    annotationLabels,
    visibleAnnotationLabels,
    selectedAnnotationLabel,
    annotationCountMap,
    onLabelClick,
    handleOnCountClick,
    handleLabelColorChange,
    labelListItemEnhancer,
    getRenderIcon,
    shouldShowTaskQuestionnaire
  } = useLabelListData({
    annotationsTabKey: TabsM3.ANNOTATIONS.key,
    filterText: inputValue,
    onOpenTaskQuestionnaire: openTaskQuestionnaire
  });
  const handleSelectChange = useCallback((params: OnChangeParams) => {
    setSelectedValue(params.value);
    const selectedOption = params.value[0];
    if (selectedOption?.value === TASK_QUESTIONNAIRE_ID) {
      onLabelClick(selectedOption.value.toString());
      setSelectedValue([]);
      setInputValue('');
      return;
    }
    const nextValue = selectedOption?.label?.toString() ?? '';
    setInputValue(nextValue);
  }, [onLabelClick]);
  const handleInputChange = useCallback((event: React.SyntheticEvent<HTMLInputElement>) => {
    const nextValue = event.currentTarget?.value ?? '';
    setInputValue(nextValue);
  }, []);
  const labelEndEnhancer = useCallback((labelTitle: string) => {
    const label = annotationLabels.find(annotationLabel => annotationLabel.title === labelTitle);
    const isRequiredFrameClassificationLabel = !!label?.isRequired && isFrameClassificationLabel(label);
    return <>
          {labelListItemEnhancer?.(labelTitle)}
          {isRequiredFrameClassificationLabel ? <RequiredLabelBadge /> : null}
        </>;
  }, [annotationLabels, labelListItemEnhancer]);
  const hideCountTagForLabel = useCallback((labelTitle: string) => {
    const label = annotationLabels.find(annotationLabel => annotationLabel.title === labelTitle);
    return !!label && !!isFrameClassificationLabel(label);
  }, [annotationLabels]);
  return <div className={css({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.sizing.scale800,
    height: '100%',
    gap: theme.sizing.scale400,
    justifyContent: 'space-between'
  })}>
      <div className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.sizing.scale400,
      flex: 1,
      overflow: 'hidden'
    })}>
        <LabelSearchSelect options={filterOptions} value={selectedValue} onChange={handleSelectChange} onInputChange={handleInputChange} />
        <LabelList annotationLabels={annotationLabels} labels={visibleAnnotationLabels} selectedLabel={selectedAnnotationLabel} annotationCountMap={annotationCountMap} onLabelClick={onLabelClick} onCountClick={handleOnCountClick} handleLabelColorChange={handleLabelColorChange} labelListItemEnhancer={labelEndEnhancer} hideCountTagForLabel={hideCountTagForLabel} getRenderIcon={getRenderIcon} taskQuestionnaireComponent={taskQuestionnaireComponent} onOpenTaskQuestionnaire={openTaskQuestionnaire} shouldShowTaskQuestionnaire={shouldShowTaskQuestionnaire} />
      </div>
      {additionalOptionsComponent && <div>{additionalOptionsComponent()}</div>}
    </div>;
};